import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const tooltipVariants = cva(
  "z-50 overflow-hidden rounded-md px-3 py-2 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      variant: {
        dark: "bg-grey-900 text-white",
        light: "bg-grey-50 text-grey-900",
        error: "bg-error-700 text-grey-50",
      },
      size: {
        sm: "max-w-[200px]",
        md: "max-w-[280px]",
        lg: "max-w-[360px]",
      },
    },
    defaultVariants: {
      variant: "dark",
      size: "md",
    },
  }
);

const arrowVariants = cva("fill-current", {
  variants: {
    variant: {
      dark: "text-grey-900",
      light: "text-grey-50",
      error: "text-error-700",
    },
  },
  defaultVariants: {
    variant: "dark",
  },
});

export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipVariants> {
  showArrow?: boolean;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      variant,
      size,
      sideOffset = 8,
      showArrow = true,
      children,
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipVariants({ variant, size, className }))}
      {...props}
    >
      {children}
      {showArrow && (
        <TooltipPrimitive.Arrow
          className={cn(arrowVariants({ variant }))}
          width={12}
          height={6}
        />
      )}
    </TooltipPrimitive.Content>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Simple text tooltip header
const TooltipHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold text-xs leading-tight", className)}
    {...props}
  />
));
TooltipHeader.displayName = "TooltipHeader";

// Tooltip description for multi-line content
const TooltipDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-xs opacity-90 mt-1 leading-relaxed", className)}
    {...props}
  />
));
TooltipDescription.displayName = "TooltipDescription";

// Convenience component for simple tooltips
interface TooltipProps extends VariantProps<typeof tooltipVariants> {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
  showArrow?: boolean;
}

const Tooltip = ({
  children,
  content,
  side = "top",
  align = "center",
  variant = "dark",
  size = "md",
  delayDuration = 200,
  showArrow = true,
}: TooltipProps) => (
  <TooltipRoot delayDuration={delayDuration}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent
      side={side}
      align={align}
      variant={variant}
      size={size}
      showArrow={showArrow}
    >
      {content}
    </TooltipContent>
  </TooltipRoot>
);

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  TooltipHeader,
  TooltipDescription,
  tooltipVariants,
};

